// THE ONE PLACE THIS CONSOLE WRITES TO FIRESTORE.
//
// Every queue button, every re-open, and every showcase lifecycle request — on the embedded steps, on
// the two subcollections whose status sits at the document root (showcase requests and gallery
// photos), and on the `lifecycle_requests` subcollection — goes through the functions below, and
// every one of them funnels into the SINGLE `saveEntity` call in `save`.
// That is deliberate and it is the main structural defence in this slice: the write boundary can be
// audited by reading one short file, and a new surface cannot invent its own field path without
// adding a call here first.
//
// ⚠️ THE INVARIANT IS **ONE `saveEntity` CALL SITE**, NOT ONE KIND OF WRITE. This file serves two
// shapes, and they are not interchangeable:
//
//   MERGE  — `status: "existing"` onto an existing onboarding document's status fields, addressed by
//            `OnboardingWriteTarget`: `{ brand: { submitState } }` and friends.
//   CREATE — `status: "new"` in the `lifecycle_requests` subcollection under one showcase request: a
//            fresh document per lifecycle action, because an action with no undo must leave one
//            immutable record per attempt instead of overwriting the evidence of the previous one.
//
// `save` is the only place that chooses between them, and it chooses from an explicit
// `WriteDestination`, so no caller can end up with a create where it meant a merge. Two shapes
// behind one call site is what the "one place" claim means here — do not read it as "one shape".
//
// ⚠️ NOTHING IN `cms/src` WRITES TO FIRESTORE OUTSIDE THIS FILE (zero hits for
// `setDoc|addDoc|updateDoc|deleteDoc|writeBatch|runTransaction`, and exactly one `saveEntity`). It
// uses the FireCMS data source, never `firebase/firestore` directly — nothing in this repo imports
// that, and going around the data source would also go around the collection's `onPreSave`.
//
// ⚠️ THE VALUES ARE BUILT IN THE `*.utils.ts` FILES, NOT HERE. `buildQueueIntentValues`,
// `buildLifecycleRequestValues` and friends are pure functions with no React and no FireCMS imports,
// so "what exactly does this write?" is answerable without reading any of the CMS machinery. This
// file only decides WHERE, in which shape, and what to do when the write fails.

import { DataSource, SnackbarController } from "@firecms/core";

import {
  APPLE_DELEGATED_ONBOARDING_PATH,
  AppleDelegatedOnboardingEntity,
} from "./apple_delegated_onboarding.types";
import {
  buildQueueIntentValues,
  buildReopenIntentValues,
  buildWriteErrorValues,
  parseOnboardingParentRef,
  QueueTarget,
  queueTargetLabel,
  stepFieldOf,
} from "./apple_delegated_onboarding.utils";
import {
  SHOWCASE_LIFECYCLE_REQUESTS_PATH,
  ShowcaseLifecycleActionEnum,
  ShowcaseLifecycleRequestEntity,
} from "./apple_showcase_lifecycle.types";
import {
  buildLifecycleRequestValues,
  buildLifecycleWriteErrorValues,
  ShowcaseSubject,
} from "./apple_showcase_lifecycle.utils";

/** Everything any write here needs: the FireCMS data source, and a snackbar to report on. */
export type ConsoleWriteDeps = {
  dataSource: DataSource;
  snackbarController: SnackbarController;
};

export type OnboardingWriteTarget = {
  /** The collection path the document lives in — `entity.path` from FireCMS. */
  path: string;
  /** The document id — `entity.id`. */
  entityId: string;
  /**
   * WHICH SURFACE is being written — the same `QueueTarget` the gate is evaluated against.
   *
   * ⚠️ IT IS THE TARGET, NOT THE FIELD PATH, AND THE DIFFERENCE IS A BUG THIS FILE USED TO HAVE
   * WAITING IN IT. Callers previously passed `step: OnboardingStepName | null`, where `null` meant
   * "status fields at the document root" — fine while the showcase subcollection was the only such
   * surface, because the label could be derived from `null`. With gallery photos as a second one,
   * that derivation would have announced a queued gallery photo as "Showcase request queued". So the
   * caller names the target and this file derives BOTH the field path (`stepFieldOf`) and the label
   * (`queueTargetLabel`) from the one definition in `apple_delegated_onboarding.utils.ts`.
   */
  queueTarget: QueueTarget;
};

/**
 * Writes `submitState: 'QUEUED'` for one step. THE ONLY TRANSITION THIS CONSOLE CAUSES.
 *
 * The whole transport is this one write: a scheduled backend drain claims `QUEUED` steps
 * transactionally, calls Apple, and writes the result back onto the same document. There is no HTTP
 * call, no client to build, and nothing to poll — the CMS re-renders from the same document.
 *
 * ⚠️ ON FAILURE IT RECORDS `writeError`. That field exists on the contract for exactly this case
 * ("I could not even enqueue"), is CMS-owned, and is never read by the backend. Without it a failed
 * enqueue leaves nothing behind but a snackbar the staff member has already dismissed, and the step
 * looks untouched. The recovery write is itself best-effort: if the document is unreachable it will
 * fail too, and the snackbar is then the only signal — which is honest, because at that point
 * nothing about this document can be written at all.
 *
 * Returns whether the intent write landed, so a caller can keep its pending flag honest.
 */
export const queueOnboardingStep = async (
  deps: ConsoleWriteDeps,
  target: OnboardingWriteTarget,
): Promise<boolean> => {
  const label = queueTargetLabel(target.queueTarget);
  try {
    await saveIntent(
      deps,
      target,
      buildQueueIntentValues(stepFieldOf(target.queueTarget), new Date()),
    );
    deps.snackbarController.open({
      type: "success",
      message: `${label} queued. The drain picks it up within about 5 minutes and the status updates on its own.`,
    });
    return true;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Unknown error";
    await recordOnboardingWriteError(deps, target, message);
    deps.snackbarController.open({
      type: "error",
      message: `Could not queue ${label.toLowerCase()}: ${message}. Nothing was queued and no Apple call will happen.`,
    });
    return false;
  }
};

/**
 * Writes `submitState: 'DRAFT'` to re-open a parked or rejected step.
 *
 * ⚠️ THIS IS NOT A RETRY, AND THE SEPARATION IS THE SAFETY FEATURE. Getting a `NEEDS_ATTENTION` step
 * back into play deliberately takes two deliberate presses — re-open, then queue — because that
 * state means Apple's outcome is UNKNOWN and a blind retry can mint a second live brand, image,
 * location or showcase under a paying client's Apple company.
 *
 * It writes `DRAFT` and nothing else. It does NOT clear `failureCode` / `failureMessage`: those are
 * backend-owned, they are the only record of what went wrong, and the drain overwrites them itself
 * on its next claim. It does NOT reset `attempts`, so a step at the cap stays blocked — only the
 * backend may lift that.
 */
export const reopenOnboardingStep = async (
  deps: ConsoleWriteDeps,
  target: OnboardingWriteTarget,
): Promise<boolean> => {
  const label = queueTargetLabel(target.queueTarget);
  try {
    await saveIntent(deps, target, buildReopenIntentValues(stepFieldOf(target.queueTarget)));
    deps.snackbarController.open({
      type: "success",
      message: `${label} is back to Draft. The previous failure is kept on the step — read it before queueing again.`,
    });
    return true;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Unknown error";
    deps.snackbarController.open({
      type: "error",
      message: `Could not re-open ${label.toLowerCase()}: ${message}. The step is unchanged.`,
    });
    return false;
  }
};

/**
 * Enqueue ONE showcase lifecycle action — extend, deactivate or delete. THE ONLY WRITE THAT SURFACE
 * CAUSES.
 *
 * ⚠️ IT CREATES A NEW DOCUMENT EVERY TIME (`kind: "create"` → `status: "new"`), never updates one.
 * For an action with no undo, each attempt has to leave its own immutable record: who asked, what
 * they were looking at, and what the backend then found. Reusing a document would overwrite the
 * evidence of the previous attempt, which is precisely the evidence anyone investigating would want.
 * This is why the file has a create shape at all, and it is the one thing not to "simplify" into the
 * targeted merge above.
 *
 * ⚠️ IT DOES NOT CALL APPLE, AND IT CANNOT. The whole transport is this one write: a backend drain
 * claims `QUEUED` requests, reads the showcase back from Apple, re-checks every gate server-side, and
 * writes the result onto the same document. Nothing here can bypass a gate, because nothing here
 * talks to Apple.
 *
 * ⚠️ ON FAILURE IT RECORDS `writeError` — best-effort, and swallowing its own failure. Same reasoning
 * as the onboarding steps above: without it a failed enqueue leaves nothing behind but a snackbar the
 * operator has already dismissed.
 *
 * Returns whether the write landed, so a caller can keep its pending flag honest.
 */
export const queueShowcaseLifecycleAction = async (
  deps: ConsoleWriteDeps,
  target: {
    /** The showcase-request document's collection path and id — `entity.path` / `entity.id`. */
    showcaseRequestPath: string;
    showcaseRequestId: string;
    action: ShowcaseLifecycleActionEnum;
    subject: ShowcaseSubject;
    requestedEndDate?: string;
    deleteCreative?: boolean;
    requestedBy?: string;
  },
): Promise<boolean> => {
  const path = lifecycleRequestsPath(target.showcaseRequestPath, target.showcaseRequestId);
  const values = buildLifecycleRequestValues({
    action: target.action,
    subject: target.subject,
    requestedEndDate: target.requestedEndDate,
    deleteCreative: target.deleteCreative,
    requestedBy: target.requestedBy,
    now: new Date(),
  });

  try {
    await save(deps, { kind: "create", path }, values);
    deps.snackbarController.open({
      type: "success",
      message: `${describeAction(target.action)} queued. The backend checks with Apple before it does anything, and the result appears on this record.`,
    });
    return true;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Unknown error";
    await recordLifecycleWriteError(deps, path, values, message);
    deps.snackbarController.open({
      type: "error",
      message: `Could not queue this ${describeAction(target.action).toLowerCase()}: ${message}. NOTHING was sent to Apple and the showcase is unchanged.`,
    });
    return false;
  }
};

/**
 * Reads the PARENT onboarding document for a showcase request, so the showcase gate can repeat the
 * backend's brand and location id pins before writing rather than after.
 *
 * Neither FireCMS's entity-action props nor its custom-view props hand a subcollection its parent's
 * values, only the path — hence the fetch. Returns `null` on any failure, and the gate degrades to
 * "the pins were not checked here" rather than refusing: the backend checks all of them
 * authoritatively before it calls Apple, so a failed read is a loss of warning, not of safety.
 */
export const fetchOnboardingParent = async (
  dataSource: DataSource,
  childPath: string | null | undefined,
): Promise<Partial<AppleDelegatedOnboardingEntity> | null> => {
  const ref = parseOnboardingParentRef(childPath);
  if (!ref) return null;
  try {
    const entity = await dataSource.fetchEntity<Partial<AppleDelegatedOnboardingEntity>>(ref);
    return entity?.values ?? null;
  } catch {
    return null;
  }
};

/**
 * WHERE a write lands, and in WHICH of the two shapes.
 *
 * ⚠️ THE DISCRIMINANT IS THE WHOLE POINT. Making every caller name the shape it wants is what lets
 * one `saveEntity` serve both a targeted merge and a create without either being able to silently
 * become the other: a `merge` cannot omit its `entityId`, and a `create` has nowhere to put one.
 */
type WriteDestination =
  | { kind: "merge"; path: string; entityId: string }
  | { kind: "create"; path: string };

/**
 * THE SINGLE `saveEntity` CALL. The whole console's Firestore write boundary is this function.
 *
 * ⚠️ `merge` → `status: "existing"`, which is a `merge: true` write underneath — the Firestore
 * delegate's `saveEntity` is `setDoc(ref, values, { merge: true })`, which merges maps RECURSIVELY.
 * That is what makes `{ brand: { submitState } }` touch exactly `brand.submitState` and leave
 * `brand.apple`, `brand.attempts` and a staff edit in progress untouched — the same targeted-merge
 * discipline the backend applies from its side.
 *
 * ⚠️ `create` → `status: "new"` with NO `entityId`, so FireCMS mints the id. Only the lifecycle
 * requests use it, and only because each attempt must be its own immutable record.
 *
 * ⚠️ `collection` IS DELIBERATELY NOT PASSED. FireCMS resolves it from `path` via the navigation
 * controller anyway, so nothing is lost; passing it would mean importing the collection definitions
 * into the module they import, and the resulting cycle is a worse problem than the one it solves.
 */
const save = async (
  deps: ConsoleWriteDeps,
  destination: WriteDestination,
  values: Record<string, unknown>,
): Promise<void> => {
  await deps.dataSource.saveEntity({
    path: destination.path,
    entityId: destination.kind === "merge" ? destination.entityId : undefined,
    values,
    status: destination.kind === "merge" ? "existing" : "new",
  });
};

/** A targeted merge onto one onboarding document, addressed the way the step surfaces address it. */
const saveIntent = async (
  deps: ConsoleWriteDeps,
  target: OnboardingWriteTarget,
  values: Record<string, unknown>,
): Promise<void> => {
  await save(deps, { kind: "merge", path: target.path, entityId: target.entityId }, values);
};

/** Best-effort `writeError`. Swallows its own failure: the caller has already reported the real one. */
const recordOnboardingWriteError = async (
  deps: ConsoleWriteDeps,
  target: OnboardingWriteTarget,
  message: string,
): Promise<void> => {
  try {
    await saveIntent(deps, target, buildWriteErrorValues(stepFieldOf(target.queueTarget), message));
  } catch {
    // Nothing to do. If this document cannot be written at all, the snackbar is the only channel
    // left, and the caller opens it.
  }
};

/** Best-effort `writeError`. Swallows its own failure — the caller has already reported the real one. */
const recordLifecycleWriteError = async (
  deps: ConsoleWriteDeps,
  path: string,
  attempted: ShowcaseLifecycleRequestEntity,
  message: string,
): Promise<void> => {
  try {
    await save(deps, { kind: "create", path }, buildLifecycleWriteErrorValues(attempted, message));
  } catch {
    // Nothing to do. If this collection cannot be written at all, the snackbar is the only channel
    // left, and the caller opens it.
  }
};

const describeAction = (action: ShowcaseLifecycleActionEnum): string => {
  if (action === ShowcaseLifecycleActionEnum.DEACTIVATE) return "Deactivation";
  if (action === ShowcaseLifecycleActionEnum.DELETE) return "Deletion";
  return "Extension";
};

/**
 * The path a root-collection queue button writes to. Exported so the collection definition and the
 * verification checklist name the same string, rather than two copies that can drift.
 */
export const ONBOARDING_WRITE_PATH = APPLE_DELEGATED_ONBOARDING_PATH;

/** The `lifecycle_requests` subcollection path under one showcase-request document. */
export const lifecycleRequestsPath = (
  showcaseRequestPath: string,
  showcaseRequestId: string,
): string => `${showcaseRequestPath}/${showcaseRequestId}/${SHOWCASE_LIFECYCLE_REQUESTS_PATH}`;
